import { useAppVariant } from "@/composables/useAppVariant";
import type { DirectiveBinding } from "vue";
import { watch } from "vue";

/**
 * Vue directive for variant-specific rendering
 *
 * Usage in templates:
 * v-variant="'gaiaeni'" - renders only in gaiaeni app
 * v-variant="'gaia'" - renders only in gaia app
 * v-variant="{ gaiaeni: true, gaia: false }" - custom config
 */

const toggleElementVisibility = (el: HTMLElement, shouldShow: boolean) => {
  if (!shouldShow) {
    el.style.display = "none";
  } else {
    el.style.display = "";
  }
};

const checkVariant = (binding: DirectiveBinding, appVariant: string): boolean => {
  if (typeof binding.value === "string") {
    const result = appVariant === binding.value;
    return result;
  } else if (typeof binding.value === "object") {
    const result = binding.value[appVariant as keyof typeof binding.value] ?? false;
    return result;
  }
  return true;
};

export const variantDirective = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const { appVariant } = useAppVariant();

    const updateVisibility = () => {
      const shouldShow = checkVariant(binding, appVariant.value);
      toggleElementVisibility(el, shouldShow);
    };

    // Initial check
    updateVisibility();

    // Watch for variant changes
    watch(() => appVariant.value, updateVisibility);
  },

  updated(el: HTMLElement, binding: DirectiveBinding) {
    const { appVariant } = useAppVariant();
    const shouldShow = checkVariant(binding, appVariant.value);
    toggleElementVisibility(el, shouldShow);
  },
};
